import { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, PUBLIC_APP_URL } from "../lib/api";
import { DiscoverySummary } from "../components/rm/DiscoverySummary";
import AdminShell from "../components/AdminShell";
import { Upload, Trash2, FileText, Sparkles, ShieldCheck, Loader2, ArrowLeft, Save, ExternalLink, Send, Copy, Check, Download, Eye, RefreshCw, Gift } from "lucide-react";
import { useAnalysisRunner, AnalysisProgressPanel, phaseLabel } from "../lib/analysisRunner";
import { BUSINESS_SNAPSHOT_QUESTIONS } from "../lib/businessSnapshotQA";
import { CAPITAL_PURPOSE_LABEL } from "../lib/quizFunderPreview";

const SECTORS = ["Fintech", "Agritech", "Healthtech", "Cleantech", "Logistics", "E-commerce", "Manufacturing", "Edtech", "Hospitality"];
const COUNTRIES = ["Nigeria", "Kenya", "Ghana", "South Africa", "Egypt", "Rwanda", "Senegal", "Ethiopia", "Tanzania", "Uganda"];
const STAGES = ["Idea", "MVP", "Revenue", "Growth"];

const ipt = "w-full rounded-lg border border-neutral-800 bg-neutral-900 px-3 py-2.5 text-sm text-neutral-100 outline-none focus:border-[#F59E0B]";
const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-xs font-semibold uppercase tracking-wider text-neutral-400">{label}</span>
    <div className="mt-1">{children}</div>
  </label>
);

const REVENUE_STAGE_LABELS = {
  pre_revenue: "Pre-revenue (KES 0 – 10M p.a.)",
  early_revenue: "Early revenue (KES 10M – 50M p.a.)",
  growth: "Growth (over KES 50M p.a.)",
};
const QUIZ_STAGE_LABELS = { pre_revenue: "Pre-revenue", post_revenue: "Post-revenue" };
const QUIZ_BAND_LABELS = {
  lt_100k: "Less than KES 100K / month",
  "100k_1m": "KES 100K – 1M / month",
  "1m_5m": "KES 1M – 5M / month",
  "5m_15m": "KES 5M – 15M / month",
  gt_15m: "KES 15M+ / month",
};
const QUIZ_PROFIT_LABELS = { yes: "Yes", no: "No", not_sure: "Not sure" };
const QUIZ_DOC_LABELS = {
  bank_statements: "Bank statements",
  pitch_deck: "Pitch deck",
  mgmt_accounts: "Management accounts",
  financial_model: "Financial model",
  audited_financials: "Audited financials",
  certificate: "Company certificate",
};
const QUIZ_RAISE_LABELS = {
  under_1m: "Under KES 1M", "1m_5m": "KES 1M – 5M", "5m_15m": "KES 5M – 15M", "15m_25m": "KES 15M – 25M",
  "25m_50m": "KES 25M – 50M", "50m_100m": "KES 50M – 100M", "100m_500m": "KES 100M – 500M", over_500m: "Over KES 500M",
};
const fmtQuizDate = (iso) => (iso ? new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");

export default function AdminBusinessDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [sme, setSme] = useState(null);
  const [rmDiscovery, setRmDiscovery] = useState(null);
  const [docs, setDocs] = useState([]);
  const [team, setTeam] = useState([]);
  const [matches, setMatches] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const [verify, setVerify] = useState({ legal_name: "", registration_number: "", director_name: "", nda_accepted: true });
  const [uploading, setUploading] = useState(false);

  // Invite state
  const [invites, setInvites] = useState([]);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteBusy, setInviteBusy] = useState(false);
  const [copiedTok, setCopiedTok] = useState(null);
  const [crrBusy, setCrrBusy] = useState(false);
  const [owner, setOwner] = useState(null);

  const load = useCallback(async () => {
    const [a, b, c] = await Promise.all([
      api.get(`/admin/businesses/${id}`),
      api.get(`/admin/businesses/${id}/matches`).catch(() => ({ data: { matches: [], by_type: {}, total: 0 } })),
      api.get(`/admin/businesses/${id}/invites`).catch(() => ({ data: [] })),
    ]);
    setSme(a.data.sme);
    setRmDiscovery(a.data.rm_discovery || null);
    setOwner(a.data.owner || null);
    setDocs(a.data.documents || []);
    setTeam(a.data.team || []);
    setMatches(b.data);
    setInvites(c.data || []);
  }, [id]);

  useEffect(() => { load(); }, [load]);

  // If the admin deep-linked from /admin/users with a #documents hash, scroll
  // to the docs card once the data has loaded. Native browser hash-scroll fires
  // before the page has the content mounted, so we re-trigger it here.
  useEffect(() => {
    if (!sme) return;
    const hash = window.location.hash.replace("#", "");
    if (!hash) return;
    const el = document.getElementById(hash);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [sme]);

  const save = async () => {
    if (!sme) return;
    setBusy(true); setMsg("");
    try {
      const payload = {
        company_name: sme.company_name || "", website: sme.website || "",
        sector: sme.sector, country: sme.country, stage: sme.stage,
        ticket_min: Number(sme.ticket_min) || 0, ticket_max: Number(sme.ticket_max) || 0,
        value_prop: sme.value_prop || "",
        revenue_annual: Number(sme.revenue_annual) || 0,
        employees: Number(sme.employees) || 1,
        sdgs: sme.sdgs || [],
        contact_name: sme.contact_name || "", contact_email: sme.contact_email || "",
      };
      await api.put(`/admin/businesses/${id}`, payload);
      setMsg("Saved.");
      await load();
    } catch (e) {
      setMsg(e.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("doc_type", "pitch_deck");
      await api.post(`/admin/businesses/${id}/documents`, fd);
      await load();
    } catch (e) {
      setMsg(e.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const removeDoc = async (docId) => {
    if (!confirm("Delete this document?")) return;
    await api.delete(`/admin/businesses/${id}/documents/${docId}`);
    await load();
  };

  const analyzer = useAnalysisRunner({
    startEndpoint: `/admin/businesses/${id}/analyze`,
    onDone: async () => {
      setMsg("AI analysis complete.");
      await load();
    },
    onError: (m) => setMsg(m),
  });
  const runAnalyze = () => { setMsg(""); analyzer.run(); };

  // Re-runs the document extractor on every persisted upload so improvements
  // to the extractor (new section markers, OCR boosts, etc.) flow into the
  // analysis WITHOUT making the founder re-upload anything.
  const [rescanBusy, setRescanBusy] = useState(false);
  const [freeBusy, setFreeBusy] = useState(false);
  const reportName = (sme?.kind === "project") ? "Project Readiness Report" : "Capital Readiness Report";
  const grantFreeReport = async () => {
    if (!window.confirm(`Grant ${sme.company_name || "this founder"} complimentary access to their ${reportName}? They will be emailed and moved to Step 2.`)) return;
    setFreeBusy(true);
    try {
      const { data } = await api.post(`/admin/businesses/${id}/grant-free-report`);
      setSme((prev) => ({ ...prev, readiness_report_unlocked: true, free_report_granted_at: data.free_report_granted_at }));
      setMsg(data.emailed ? "Free access granted — founder emailed." : "Free access granted (no owner email on file).");
    } catch (e) { setMsg(e?.response?.data?.detail || "Couldn't grant free access"); }
    finally { setFreeBusy(false); }
  };
  const [rescanReport, setRescanReport] = useState(null);
  const rescanPollRef = useRef(null);

  const _clearRescanPoll = () => {
    if (rescanPollRef.current) {
      clearInterval(rescanPollRef.current);
      rescanPollRef.current = null;
    }
  };

  const _pollRescan = async () => {
    try {
      const { data } = await api.get(`/admin/businesses/${id}/rescan-status`);
      if (!data) return;
      if (data.status === "done") {
        _clearRescanPoll();
        setRescanBusy(false);
        setRescanReport(data);
        setMsg(
          `Re-scanned ${data.docs_scanned ?? 0} documents — ` +
          `${data.rescan_updated ?? 0} got fresh extracted_text` +
          (data.docs_failed ? ` · ${data.docs_failed} failed` : "") + "."
        );
        await load();
      } else if (data.status === "failed") {
        _clearRescanPoll();
        setRescanBusy(false);
        setMsg("Re-scan failed: " + (data.error || "unknown"));
      } else {
        // still running — surface progress
        setRescanReport(data);
      }
    } catch (e) {
      // transient poll failure; keep polling
      if (process.env.NODE_ENV !== "production") console.debug("[rescan] poll failed", e?.message);
    }
  };

  const runRescanDocs = async () => {
    setRescanBusy(true);
    setRescanReport(null);
    setMsg("Re-scan started — running in the background…");
    _clearRescanPoll();
    try {
      await api.post(`/admin/businesses/${id}/rescan-documents`);
      // Kick off poll loop; first tick fires immediately.
      _pollRescan();
      rescanPollRef.current = setInterval(_pollRescan, 3000);
    } catch (e) {
      setMsg(e?.response?.data?.detail || "Re-scan failed to start.");
      setRescanBusy(false);
    }
  };

  // Ensure poll timer is cleared on unmount.
  useEffect(() => () => _clearRescanPoll(), []);
  // Admin discretionary grant: credits + optional unlock. Full audit trail
  // lives server-side in `admin_actions`.
  const [grant, setGrant] = useState({ credits: 3, unlock: true, reason: "" });
  const [grantBusy, setGrantBusy] = useState(false);
  const runGrantCredits = async () => {
    setGrantBusy(true);
    setMsg("");
    try {
      const { data } = await api.post(`/admin/businesses/${id}/grant-credits`, {
        credits: Number(grant.credits) || 0,
        unlock_report: !!grant.unlock,
        reason: grant.reason || "",
      });
      setMsg(
        `Grant applied — balance now ${data.credits_balance}, ` +
        `report ${data.unlocked ? "unlocked" : "still locked"}.`
      );
      setGrant({ credits: 3, unlock: true, reason: "" });
      await load();
    } catch (e) {
      setMsg(e?.response?.data?.detail || "Grant failed.");
    } finally {
      setGrantBusy(false);
    }
  };

  const runVerify = async () => {
    setBusy(true); setMsg("");
    try {
      await api.post(`/admin/businesses/${id}/verify`, verify);
      setMsg("Business verified. Smart alerts triggered to matching funders.");
      await load();
    } catch (e) {
      setMsg(e.response?.data?.detail || "Verify failed");
    } finally { setBusy(false); }
  };

  const removeBiz = async () => {
    if (!confirm("Delete this business and all its documents?")) return;
    await api.delete(`/admin/businesses/${id}`);
    nav("/admin/businesses");
  };

  const claimUrl = (token) => `${PUBLIC_APP_URL}/founder/claim?token=${token}`;

  const createInvite = async () => {
    setInviteBusy(true);
    try {
      const body = {};
      if (inviteEmail.trim()) body.founder_email = inviteEmail.trim();
      else if (sme.contact_email) body.founder_email = sme.contact_email;
      await api.post(`/admin/businesses/${id}/invite`, body);
      setInviteEmail("");
      await load();
    } catch (e) {
      setMsg(e.response?.data?.detail || "Couldn't create invite");
    } finally { setInviteBusy(false); }
  };

  const revokeInvite = async (tok) => {
    if (!confirm("Revoke this invite?")) return;
    await api.delete(`/admin/invites/${tok}`);
    await load();
  };

  const copyLink = async (tok) => {
    try {
      await navigator.clipboard.writeText(claimUrl(tok));
      setCopiedTok(tok);
      setTimeout(() => setCopiedTok(null), 2000);
    } catch {
      // fallback: select link text via prompt
      window.prompt("Copy this claim link", claimUrl(tok));
    }
  };

  if (!sme) return <AdminShell><p className="text-neutral-500">Loading…</p></AdminShell>;

  // Founder-owned businesses are READ-ONLY for admins — no edits, no uploads,
  // no deletes, no invites. We only let admins view + run AI analysis +
  // verify (because verification is an admin moderation action).
  const isFounderOwned = sme.managed_by_admin !== true;

  const downloadDoc = async (doc) => {
    try {
      const res = await api.get(
        `/admin/businesses/${id}/documents/${doc.doc_id}/download`,
        { responseType: "blob" });
      const blob = new Blob([res.data], { type: doc.content_type || "application/octet-stream" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = doc.filename || "document";
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch {
      setMsg("Couldn't download document.");
    }
  };

  // Capital Readiness Report PDF — admin bypass (no founder unlock gate).
  const downloadCRRPdf = async () => {
    setCrrBusy(true);
    setMsg("");
    try {
      const res = await api.get(
        `/admin/businesses/${id}/report.pdf`,
        { responseType: "blob" }
      );
      const blob = new Blob([res.data], { type: "application/pdf" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      const slug = (sme.company_name || "clinq-ccr").replace(/\s+/g, "-").toLowerCase();
      a.download = `${slug}-ccr-report.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (e) {
      setMsg(e?.response?.data?.detail || "Couldn't download the readiness report PDF.");
    } finally {
      setCrrBusy(false);
    }
  };

  return (
    <AdminShell>
      <button onClick={() => nav("/admin/businesses")} className="mb-4 inline-flex items-center gap-1 text-sm text-neutral-400 hover:text-neutral-100">
        <ArrowLeft className="h-4 w-4"/> Back to all businesses
      </button>

      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold" data-testid="admin-biz-title">{sme.company_name || "(Untitled)"}</h1>
          <p className="mt-1 text-sm text-neutral-400">
            {sme.sector} · {sme.country} · {sme.stage}
            {sme.contact_name && <span className="ml-2">· Contact: {sme.contact_name} ({sme.contact_email})</span>}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {sme.free_report_granted_at ? (
            <span className="inline-flex items-center gap-1 rounded-full bg-violet-500/15 px-3 py-1 text-xs font-bold uppercase text-violet-300"
              title={`Granted ${new Date(sme.free_report_granted_at).toLocaleDateString("en-GB")}${sme.free_report_granted_by ? ` by ${sme.free_report_granted_by}` : ""}`}
              data-testid="admin-biz-free-access-badge">
              <Gift className="h-3 w-3" /> Free {reportName} access
            </span>
          ) : (
            <button type="button" onClick={grantFreeReport} disabled={freeBusy} data-testid="admin-biz-grant-free-report"
              title={sme.readiness_report_unlocked ? "Already unlocked (paid) — grant will only label the account as complimentary" : `Bypass payment: unlock the ${reportName}, add the 3 re-run credits, email the founder and move them to Step 2`}
              className="inline-flex items-center gap-1.5 rounded-full border border-violet-500/40 bg-violet-500/10 px-3 py-1 text-xs font-bold text-violet-200 hover:bg-violet-500/20 disabled:opacity-50">
              {freeBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Gift className="h-3 w-3" />} Grant free report
            </button>
          )}
          <span className={"rounded-full px-3 py-1 text-xs font-bold uppercase " +
            (sme.status === "verified" ? "bg-green-500/15 text-green-400"
             : sme.status === "deep_dive" ? "bg-sky-500/15 text-sky-400"
             : "bg-amber-500/15 text-amber-400")}>
            {sme.status}
          </span>
          {owner?.dormant_email_sent_at && (
            <span data-testid="admin-biz-dormant-badge"
              className="inline-flex items-center rounded-full border border-amber-500/30 bg-amber-500/10 px-3 py-1 text-xs font-bold uppercase text-amber-300"
              title={`Dormant check-in email sent ${new Date(owner.dormant_email_sent_at).toLocaleString()}`}>
              Dormant email sent · {new Date(owner.dormant_email_sent_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
            </span>
          )}
          {isFounderOwned && (
            <span data-testid="admin-biz-readonly-badge"
              className="inline-flex items-center gap-1 rounded-full border border-sky-500/30 bg-sky-500/10 px-3 py-1 text-xs font-bold uppercase text-sky-300">
              <Eye className="h-3.5 w-3.5"/> View-only · founder-owned
            </span>
          )}
          <Link
            to={`/funder/sme/${id}`}
            data-testid="admin-biz-view-as-funder"
            className="inline-flex items-center gap-1 rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-3 py-1.5 text-xs font-bold text-emerald-300 hover:bg-emerald-500/20"
            title="Inspect this business through the funder lens — full data room, no approval required.">
            <Eye className="h-3.5 w-3.5"/> View as funder
          </Link>
          {!isFounderOwned && (
            <button onClick={removeBiz} data-testid="admin-biz-delete"
              className="rounded-lg border border-red-900/40 bg-red-950/40 px-3 py-1.5 text-xs font-semibold text-red-400 hover:bg-red-900/50">
              <Trash2 className="inline h-3.5 w-3.5 mr-1"/>Delete
            </button>
          )}
        </div>
      </div>

      {msg && <div className="mt-4 rounded-lg border border-neutral-800 bg-neutral-900 px-4 py-2 text-sm text-neutral-300" data-testid="admin-biz-msg">{msg}</div>}

      <div className="mt-6 grid grid-cols-1 gap-5 lg:grid-cols-3">
        {/* Readiness + matches */}
        <div className="rounded-2xl border border-neutral-800 bg-neutral-900 p-6">
          <p className="text-xs font-bold uppercase tracking-wider text-neutral-500">Readiness</p>
          <div className="mt-2 flex items-baseline gap-2">
            <span className="text-6xl font-extrabold text-[#F59E0B]">{sme.readiness_score || 0}</span>
            <span className="text-sm text-neutral-500">/ 100</span>
          </div>
          <button onClick={runAnalyze} disabled={busy || analyzer.running} data-testid="admin-biz-analyze"
            title={isFounderOwned
              ? "Re-scores this founder's business against every uploaded document. Free — no re-run credit is deducted. Shows the true recomputed score."
              : undefined}
            className="mt-4 inline-flex w-full items-center justify-center gap-1.5 rounded-lg bg-[#F59E0B] px-4 py-2 text-sm font-bold text-neutral-950 hover:bg-[#EAB308] disabled:cursor-not-allowed disabled:opacity-50">
            {(busy || analyzer.running) ? <Loader2 className="h-4 w-4 animate-spin"/> : <Sparkles className="h-4 w-4"/>}
            {analyzer.running
              ? `${phaseLabel(analyzer.phase)}…`
              : isFounderOwned
                ? "Re-run analysis (free, admin)"
                : "Run analysis"}
          </button>
          {isFounderOwned && (
            <p className="mt-1.5 text-[11px] leading-snug text-neutral-500" data-testid="admin-biz-analyze-note">
              Founder-owned: this re-scores all documents without using the founder&apos;s credits and
              shows the true recomputed score. Re-scan only refreshes extracted text — it never changes the score.
            </p>
          )}
          <button
            onClick={runRescanDocs}
            disabled={rescanBusy}
            data-testid="admin-biz-rescan-docs"
            title="Re-runs the document extractor over every uploaded file (no founder action required). Use after extractor improvements ship."
            className="mt-2 inline-flex w-full items-center justify-center gap-1.5 rounded-lg border border-neutral-700 bg-neutral-800 px-4 py-2 text-xs font-bold text-neutral-200 hover:border-neutral-600 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {rescanBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin"/> : <RefreshCw className="h-3.5 w-3.5"/>}
            Re-scan documents
          </button>
          {rescanReport && (
            <div
              data-testid="admin-biz-rescan-report"
              className="mt-3 rounded-lg border border-neutral-800 bg-neutral-950 p-3 text-[11px] text-neutral-300"
            >
              <p className="font-semibold text-neutral-100">
                {rescanReport.documents_updated}/{rescanReport.documents_scanned} updated
              </p>
              {(rescanReport.results || []).slice(0, 8).map((r) => (
                <p key={r.doc_id} className="mt-1 truncate">
                  <span
                    className={
                      r.status === "updated"
                        ? "text-emerald-400"
                        : r.status === "unchanged"
                          ? "text-neutral-500"
                          : "text-rose-400"
                    }
                  >
                    {r.status === "updated" ? "✓" : r.status === "unchanged" ? "·" : "✗"}
                  </span>{" "}
                  <span className="text-neutral-200">{r.filename}</span>
                  {r.added_markers?.length > 0 && (
                    <span className="ml-1 text-emerald-300">
                      +{r.added_markers.join(", ")}
                    </span>
                  )}
                </p>
              ))}
            </div>
          )}
          <AnalysisProgressPanel busy={analyzer.running} phase={analyzer.phase} testIdPrefix="admin-analysis" countdown />
          {analyzer.error && (
            <p className="mt-2 text-xs font-semibold text-rose-400" data-testid="admin-analysis-error">{analyzer.error}</p>
          )}
        </div>

        {/* Matches by type */}
        <div className="rounded-2xl border border-neutral-800 bg-neutral-900 p-6 lg:col-span-2" data-testid="admin-biz-matches-card">
          <p className="text-xs font-bold uppercase tracking-wider text-neutral-500">Funder matches</p>
          <p className="mt-1 text-sm text-neutral-400">
            We can connect this business to <b className="text-neutral-100">{matches?.total || 0}</b> funders.
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            {Object.entries(matches?.by_type || {}).map(([t, n]) => (
              <span key={t} data-testid={`admin-biz-match-${t.toLowerCase()}`}
                className="rounded-full border border-[#F59E0B]/30 bg-[#F59E0B]/10 px-3 py-1 text-xs font-bold text-[#F59E0B]">
                {n} {t}
              </span>
            ))}
          </div>
          {matches?.matches?.length > 0 && (
            <ul className="mt-4 divide-y divide-neutral-800 max-h-64 overflow-y-auto">
              {matches.matches.slice(0, 12).map(m => (
                <li key={m.funder_id} className="py-2 flex items-center justify-between gap-3 text-sm">
                  <div className="min-w-0">
                    <p className="font-semibold text-neutral-200 truncate">{m.name}</p>
                    <p className="text-xs text-neutral-500">{m.type} · ${(m.ticket_min/1000).toFixed(0)}K–${(m.ticket_max/1000).toFixed(0)}K</p>
                  </div>
                  <span className="text-[#F59E0B] font-bold">{m.fit_score}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Capital Readiness Report download — admins can grab any founder's
          PDF without needing the founder to unlock it. */}
      <div
        className="mt-6 rounded-2xl border border-neutral-800 bg-neutral-900 p-6"
        data-testid="admin-biz-crr-card"
      >
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-neutral-500">
              Capital Readiness Report
            </p>
            <h2 className="mt-1 text-lg font-bold text-neutral-100">
              Founder report (PDF)
            </h2>
            <p className="mt-1 max-w-xl text-sm text-neutral-400">
              The full Capital Readiness Score™ report this founder gets when
              they unlock it — pillar breakdown, capital structure, funder
              matches, and 90-day roadmap.
              {!sme.ai_analysis && (
                <span className="mt-1 block text-amber-400">
                  No analysis yet — run analysis above first.
                </span>
              )}
            </p>
          </div>
          <button
            type="button"
            onClick={downloadCRRPdf}
            disabled={!sme.ai_analysis || crrBusy}
            data-testid="admin-biz-crr-download-btn"
            className="inline-flex shrink-0 items-center gap-2 rounded-lg bg-[#F59E0B] px-4 py-2.5 text-sm font-bold text-neutral-950 hover:bg-[#EAB308] disabled:cursor-not-allowed disabled:opacity-50"
          >
            {crrBusy ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Download className="h-4 w-4" />
            )}
            Download PDF
          </button>
        </div>
      </div>

      {/* Admin grant panel — discretionary credit + unlock override.
          Used for test-payment recoveries, comped accounts, and support
          escalations. Every grant writes an audit row to admin_actions. */}
      <div
        className="mt-6 rounded-2xl border border-emerald-800/40 bg-neutral-900 p-6"
        data-testid="admin-biz-grant-card"
      >
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <p className="text-xs font-bold uppercase tracking-wider text-emerald-400">
              Admin grant
            </p>
            <h2 className="mt-1 text-lg font-bold text-neutral-100">
              Grant re-run credits &amp; unlock report
            </h2>
            <p className="mt-1 max-w-xl text-sm text-neutral-400">
              Current: <span className="font-semibold text-neutral-200" data-testid="admin-biz-grant-balance">
                {Number(sme.readiness_credits || 0)} credits
              </span>
              {" · "}
              <span className={sme.readiness_report_unlocked ? "text-emerald-400" : "text-neutral-400"}
                    data-testid="admin-biz-grant-unlock-status">
                {sme.readiness_report_unlocked ? "Report unlocked" : "Report locked"}
              </span>
              . Use this for test-payment recoveries, comped accounts, or support escalations.
            </p>
          </div>
        </div>

        <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-[140px_1fr_auto] md:items-end">
          <label className="block">
            <span className="text-xs font-semibold text-neutral-400">Credits to add</span>
            <input
              type="number"
              min="0"
              max="500"
              value={grant.credits}
              onChange={(e) => setGrant({ ...grant, credits: e.target.value })}
              data-testid="admin-biz-grant-credits-input"
              className="mt-1 w-full rounded-md border border-neutral-700 bg-neutral-950 px-3 py-2 text-sm text-neutral-100 focus:border-emerald-500 focus:outline-none"
            />
          </label>
          <label className="block">
            <span className="text-xs font-semibold text-neutral-400">Reason (for audit trail)</span>
            <input
              type="text"
              value={grant.reason}
              onChange={(e) => setGrant({ ...grant, reason: e.target.value })}
              placeholder="e.g. Test payment recovery for musyoki@enzimotors.com"
              data-testid="admin-biz-grant-reason-input"
              className="mt-1 w-full rounded-md border border-neutral-700 bg-neutral-950 px-3 py-2 text-sm text-neutral-100 focus:border-emerald-500 focus:outline-none"
            />
          </label>
          <button
            type="button"
            onClick={runGrantCredits}
            disabled={grantBusy || (Number(grant.credits) === 0 && !grant.unlock)}
            data-testid="admin-biz-grant-submit-btn"
            className="inline-flex shrink-0 items-center gap-2 rounded-lg bg-emerald-500 px-4 py-2.5 text-sm font-bold text-neutral-950 hover:bg-emerald-400 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {grantBusy ? <Loader2 className="h-4 w-4 animate-spin"/> : null}
            Apply grant
          </button>
        </div>

        <label className="mt-3 flex items-center gap-2 text-sm text-neutral-300">
          <input
            type="checkbox"
            checked={!!grant.unlock}
            onChange={(e) => setGrant({ ...grant, unlock: e.target.checked })}
            data-testid="admin-biz-grant-unlock-checkbox"
            className="h-4 w-4 rounded border-neutral-700 bg-neutral-950 text-emerald-500 focus:ring-emerald-500"
          />
          Also unlock Capital Readiness Report (if not already)
        </label>
      </div>

      {/* Founder journey — snapshot + pre-signup quiz answers */}
      <div className="mt-6 rounded-2xl border border-neutral-800 bg-neutral-900 p-6" data-testid="admin-biz-journey-card">
        <h2 className="text-lg font-bold">Founder Snapshot &amp; Quiz</h2>
        <div className="mt-4 grid grid-cols-1 gap-6 md:grid-cols-2">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-widest text-[#F59E0B]">Business Snapshot</p>
            <dl className="mt-2 space-y-2 text-sm">
              {[
                ["Revenue stage", REVENUE_STAGE_LABELS[sme.revenue_stage] || sme.revenue_stage || "—"],
                ["Industry", sme.sector || "—"],
                ["Founder role", (team.find(t => t.is_primary) || team[0] || {}).role || "—"],
                ["Website", sme.website || "—"],
                ["LinkedIn", sme.linkedin_url || "—"],
              ].map(([k, v]) => (
                <div key={k} className="flex items-start justify-between gap-3">
                  <dt className="shrink-0 text-neutral-500">{k}</dt>
                  <dd className="truncate text-right text-neutral-200" data-testid={`admin-biz-journey-${k.toLowerCase().replace(/\s+/g, "-")}`}>{v}</dd>
                </div>
              ))}
              <div>
                <dt className="text-neutral-500">Business summary</dt>
                <dd className="mt-1 whitespace-pre-line text-neutral-300" data-testid="admin-biz-journey-summary">
                  {sme.business_summary || "—"}
                </dd>
              </div>
            </dl>
          </div>
          {rmDiscovery && (
            <div className="md:col-span-2">
              <p className="text-[10px] font-bold uppercase tracking-widest text-[#F59E0B]">RM discovery meeting</p>
              <DiscoverySummary discovery={rmDiscovery} dark defaultOpen testid="admin-biz-discovery" />
            </div>
          )}
          <div>
            <p className="text-[10px] font-bold uppercase tracking-widest text-[#F59E0B]">Get Started quiz answers</p>
            {sme.get_started_quiz ? (
              <dl className="mt-2 space-y-2 text-sm" data-testid="admin-biz-quiz-answers">
                <div className="flex items-start justify-between gap-3">
                  <dt className="text-neutral-500">Stage selected</dt>
                  <dd className="text-neutral-200">{QUIZ_STAGE_LABELS[sme.get_started_quiz.stage] || sme.get_started_quiz.stage || "—"}</dd>
                </div>
                <div className="flex items-start justify-between gap-3">
                  <dt className="text-neutral-500">Monthly revenue band</dt>
                  <dd className="text-neutral-200" data-testid="admin-biz-quiz-revenue-band">
                    {QUIZ_BAND_LABELS[sme.get_started_quiz.revenue_band] || sme.get_started_quiz.revenue_band || "—"}
                  </dd>
                </div>
                <div className="flex items-start justify-between gap-3">
                  <dt className="text-neutral-500">Profitable?</dt>
                  <dd data-testid="admin-biz-quiz-profitable">
                    <span className={
                      "rounded-full px-2 py-0.5 text-xs font-bold " +
                      (sme.get_started_quiz.is_profitable === "yes"
                        ? "bg-emerald-500/10 text-emerald-300"
                        : sme.get_started_quiz.is_profitable === "no"
                          ? "bg-red-500/10 text-red-300"
                          : "bg-neutral-700/40 text-neutral-300")
                    }>
                      {QUIZ_PROFIT_LABELS[sme.get_started_quiz.is_profitable] || "—"}
                    </span>
                  </dd>
                </div>
                <div>
                  <dt className="text-neutral-500">Documents they said they have</dt>
                  <dd className="mt-1.5 flex flex-wrap gap-1.5" data-testid="admin-biz-quiz-docs">
                    {(sme.get_started_quiz.docs || []).length === 0
                      ? <span className="text-neutral-400">None ticked</span>
                      : sme.get_started_quiz.docs.map((d) => (
                          <span key={d} className="rounded-full bg-neutral-800 px-2 py-0.5 text-xs text-neutral-200">
                            {QUIZ_DOC_LABELS[d] || d}
                          </span>
                        ))}
                  </dd>
                </div>
                <div>
                  <dt className="text-neutral-500">Use of funds</dt>
                  <dd className="mt-1.5 flex flex-wrap gap-1.5" data-testid="admin-biz-quiz-purposes">
                    {(sme.get_started_quiz.capital_purposes || []).length === 0
                      ? <span className="text-neutral-400">—</span>
                      : sme.get_started_quiz.capital_purposes.map((p) => (
                          <span key={p} className={"rounded-full px-2 py-0.5 text-xs " +
                            (p === sme.get_started_quiz.capital_purpose_primary
                              ? "bg-[#F59E0B]/15 font-bold text-[#F59E0B]" : "bg-neutral-800 text-neutral-200")}>
                            {CAPITAL_PURPOSE_LABEL[p] || p}{p === sme.get_started_quiz.capital_purpose_primary ? " · primary" : ""}
                          </span>
                        ))}
                  </dd>
                  {sme.get_started_quiz.capital_purpose_other && (
                    <p className="mt-1.5 rounded-lg bg-neutral-800/60 px-2.5 py-1.5 text-xs italic text-neutral-300" data-testid="admin-biz-quiz-purpose-other">
                      &ldquo;{sme.get_started_quiz.capital_purpose_other}&rdquo;
                    </p>
                  )}
                </div>
                <div className="flex items-start justify-between gap-3">
                  <dt className="text-neutral-500">Industry</dt>
                  <dd className="text-neutral-200" data-testid="admin-biz-quiz-industry">
                    {sme.get_started_quiz.industry === "other" && sme.get_started_quiz.industry_other
                      ? sme.get_started_quiz.industry_other
                      : (sme.get_started_quiz.industry_label || sme.get_started_quiz.industry || "—")}
                  </dd>
                </div>
                <div className="flex items-start justify-between gap-3">
                  <dt className="text-neutral-500">Raise amount</dt>
                  <dd className="text-neutral-200" data-testid="admin-biz-quiz-raise">
                    {QUIZ_RAISE_LABELS[sme.get_started_quiz.raise_amount_bracket] || sme.get_started_quiz.raise_amount_bracket || "—"}
                  </dd>
                </div>
                <div className="flex items-start justify-between gap-3">
                  <dt className="text-neutral-500">Preliminary quiz score</dt>
                  <dd className="font-bold text-neutral-100" data-testid="admin-biz-quiz-prelim">
                    {typeof sme.get_started_quiz.preliminary_score === "number" ? `${sme.get_started_quiz.preliminary_score} / 100` : "—"}
                  </dd>
                </div>
                <div className="flex items-start justify-between gap-3">
                  <dt className="text-neutral-500">Quiz completed</dt>
                  <dd className="text-neutral-300" data-testid="admin-biz-quiz-completed">{fmtQuizDate(sme.get_started_quiz.completed_at)}</dd>
                </div>
              </dl>
            ) : (
              <p className="mt-2 text-sm text-neutral-500" data-testid="admin-biz-quiz-empty">
                Not available — this founder signed up before quiz capture was added (or skipped the quiz).
              </p>
            )}
          </div>
        </div>

        {/* Qualitative Business Snapshot Q&A — founders without a pitch deck
            answer 7 free-text questions on onboarding. */}
        {sme.business_snapshot_qa && Object.values(sme.business_snapshot_qa.answers || {}).some(Boolean) && (
          <div className="mt-6 border-t border-neutral-800 pt-5" data-testid="admin-biz-snapshot-qa">
            <p className="text-[10px] font-bold uppercase tracking-widest text-[#F59E0B]">
              Business Snapshot Q&amp;A (no pitch deck)
            </p>
            {sme.business_snapshot_qa.website && (
              <p className="mt-2 text-sm">
                <span className="text-neutral-500">Website shared: </span>
                <a href={/^https?:\/\//.test(sme.business_snapshot_qa.website) ? sme.business_snapshot_qa.website : `https://${sme.business_snapshot_qa.website}`}
                   target="_blank" rel="noreferrer"
                   className="font-semibold text-sky-300 hover:underline" data-testid="admin-biz-snapshot-qa-website">
                  {sme.business_snapshot_qa.website}
                </a>
              </p>
            )}
            <dl className="mt-3 space-y-4 text-sm">
              {BUSINESS_SNAPSHOT_QUESTIONS.map((q, i) => (
                sme.business_snapshot_qa.answers?.[q.key] ? (
                  <div key={q.key} data-testid={`admin-biz-snapshot-qa-${q.key}`}>
                    <dt className="font-semibold text-neutral-400">{i + 1}. {q.label}</dt>
                    <dd className="mt-1 whitespace-pre-line text-neutral-200">{sme.business_snapshot_qa.answers[q.key]}</dd>
                  </div>
                ) : null
              ))}
            </dl>
            {sme.business_snapshot_qa.saved_at && (
              <p className="mt-3 text-xs text-neutral-500">
                Last saved {new Date(sme.business_snapshot_qa.saved_at).toLocaleString()}
              </p>
            )}
          </div>
        )}
      </div>


      {/* Snapshot editor — admin-created businesses only. For founder-owned
          businesses this was a redundant read-only duplicate of the Founder
          Snapshot & Quiz card above (removed Jun 2026). */}
      {!isFounderOwned && (
      <div className="mt-6 rounded-2xl border border-neutral-800 bg-neutral-900 p-6">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold">Business info</h2>
          {isFounderOwned && (
            <span className="inline-flex items-center gap-1 text-xs font-semibold text-sky-300">
              <Eye className="h-3.5 w-3.5"/> Read-only
            </span>
          )}
        </div>
        <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
          <Field label="Company name">
            <input value={sme.company_name || ""} onChange={e => setSme({...sme, company_name: e.target.value})} className={ipt} data-testid="admin-biz-edit-name" disabled={isFounderOwned}/>
          </Field>
          <Field label="Website">
            <input value={sme.website || ""} onChange={e => setSme({...sme, website: e.target.value})} className={ipt} disabled={isFounderOwned}/>
          </Field>
          <Field label="Sector">
            <select value={sme.sector || ""} onChange={e => setSme({...sme, sector: e.target.value})} className={ipt} disabled={isFounderOwned}>
              {SECTORS.map(s => <option key={s}>{s}</option>)}
            </select>
          </Field>
          <Field label="Country">
            <select value={sme.country || ""} onChange={e => setSme({...sme, country: e.target.value})} className={ipt} disabled={isFounderOwned}>
              {COUNTRIES.map(c => <option key={c}>{c}</option>)}
            </select>
          </Field>
          <Field label="Stage">
            <select value={sme.stage || ""} onChange={e => setSme({...sme, stage: e.target.value})} className={ipt} disabled={isFounderOwned}>
              {STAGES.map(s => <option key={s}>{s}</option>)}
            </select>
          </Field>
          <Field label="Revenue (USD)">
            <input type="number" value={sme.revenue_annual || 0} onChange={e => setSme({...sme, revenue_annual: e.target.value})} className={ipt} disabled={isFounderOwned}/>
          </Field>
          <Field label="Ticket min">
            <input type="number" value={sme.ticket_min || 0} onChange={e => setSme({...sme, ticket_min: e.target.value})} className={ipt} disabled={isFounderOwned}/>
          </Field>
          <Field label="Ticket max">
            <input type="number" value={sme.ticket_max || 0} onChange={e => setSme({...sme, ticket_max: e.target.value})} className={ipt} disabled={isFounderOwned}/>
          </Field>
          <Field label="Employees">
            <input type="number" value={sme.employees || 1} onChange={e => setSme({...sme, employees: e.target.value})} className={ipt} disabled={isFounderOwned}/>
          </Field>
          <div className="md:col-span-2">
            <Field label="Value proposition">
              <textarea rows={3} value={sme.value_prop || ""} onChange={e => setSme({...sme, value_prop: e.target.value})} className={ipt} disabled={isFounderOwned}/>
            </Field>
          </div>
        </div>
        {!isFounderOwned && (
          <div className="mt-4 flex justify-end">
            <button onClick={save} disabled={busy} data-testid="admin-biz-save"
              className="inline-flex items-center gap-1.5 rounded-lg bg-[#F59E0B] px-5 py-2 text-sm font-bold text-neutral-950 hover:bg-[#EAB308] disabled:opacity-50">
              <Save className="h-4 w-4"/>{busy ? "Saving…" : "Save changes"}
            </button>
          </div>
        )}
      </div>
      )}

      {/* Documents */}
      <div id="documents" className="mt-6 scroll-mt-24 rounded-2xl border border-neutral-800 bg-neutral-900 p-6" data-testid="admin-biz-docs-card">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold">Documents</h2>
          {isFounderOwned && (
            <span className="inline-flex items-center gap-1 text-xs font-semibold text-sky-300">
              <Eye className="h-3.5 w-3.5"/> View-only
            </span>
          )}
        </div>
        <p className="mt-1 text-sm text-neutral-400">
          {isFounderOwned
            ? "Documents uploaded by the founder. You can download for review but cannot edit or delete them."
            : "Pitch decks, financials, legal documents."}
        </p>
        {!isFounderOwned && (
          <label className="mt-4 flex cursor-pointer items-center justify-center gap-2 rounded-lg border-2 border-dashed border-neutral-800 bg-neutral-950 px-4 py-6 text-sm text-neutral-400 hover:border-[#F59E0B]/40">
            {uploading ? <Loader2 className="h-5 w-5 animate-spin"/> : <Upload className="h-5 w-5"/>}
            <span>{uploading ? "Uploading…" : "Click to upload PDF (max 15MB)"}</span>
            <input type="file" accept="application/pdf" className="hidden" onChange={onUpload} data-testid="admin-biz-doc-upload"/>
          </label>
        )}
        {docs.length === 0 ? (
          <p className="mt-4 text-sm text-neutral-500" data-testid="admin-biz-docs-empty">
            {isFounderOwned ? "The founder hasn't uploaded any documents yet." : "No documents uploaded yet."}
          </p>
        ) : (
          <ul className="mt-4 divide-y divide-neutral-800">
            {docs.map(d => (
              <li key={d.doc_id} className="flex items-center justify-between gap-3 py-3 text-sm" data-testid={`admin-biz-doc-${d.doc_id}`}>
                <div className="flex min-w-0 items-center gap-2">
                  <FileText className="h-4 w-4 shrink-0 text-neutral-500"/>
                  <span className="truncate text-neutral-200">{d.filename}</span>
                  <span className="shrink-0 text-xs text-neutral-500">({Math.round(d.size/1024)}KB)</span>
                  {d.doc_type && (
                    <span className="shrink-0 rounded-full border border-neutral-800 bg-neutral-950 px-2 py-0.5 text-[10px] font-semibold uppercase text-neutral-400">
                      {d.doc_type.replace(/_/g, " ")}
                    </span>
                  )}
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <button
                    onClick={() => downloadDoc(d)}
                    data-testid={`admin-biz-doc-download-${d.doc_id}`}
                    className="inline-flex items-center gap-1 rounded-lg border border-neutral-800 bg-neutral-950 px-2.5 py-1.5 text-xs font-semibold text-neutral-200 hover:bg-neutral-800">
                    <Download className="h-3.5 w-3.5"/> Download
                  </button>
                  {!isFounderOwned && (
                    <button onClick={() => removeDoc(d.doc_id)} className="text-xs text-red-400 hover:text-red-300"
                            data-testid={`admin-biz-doc-delete-${d.doc_id}`}>
                      <Trash2 className="inline h-3.5 w-3.5"/>
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Invite founder (magic-link claim) — admin-managed businesses only */}
      {!isFounderOwned && (
      <div className="mt-6 rounded-2xl border border-neutral-800 bg-neutral-900 p-6" data-testid="admin-biz-invites">
        <div className="flex items-center gap-2">
          <Send className="h-4 w-4 text-[#F59E0B]"/>
          <h2 className="text-lg font-bold">Invite founder to claim this business</h2>
        </div>
        <p className="mt-1 text-sm text-neutral-400">
          Generate a one-time link that transfers ownership of <b className="text-neutral-200">{sme.company_name || "this business"}</b> to the real founder. Copy and send it however you like (email, WhatsApp, etc.). Expires in 7 days.
        </p>

        <div className="mt-4 flex flex-wrap items-center gap-2">
          <input data-testid="admin-invite-email" type="email"
            placeholder={sme.contact_email ? `Default: ${sme.contact_email}` : "founder@business.co (optional)"}
            value={inviteEmail} onChange={e => setInviteEmail(e.target.value)}
            className="flex-1 min-w-[240px] rounded-lg border border-neutral-800 bg-neutral-950 px-3 py-2 text-sm text-neutral-100 outline-none focus:border-[#F59E0B]"/>
          <button onClick={createInvite} disabled={inviteBusy} data-testid="admin-invite-create"
            className="inline-flex items-center gap-1.5 rounded-lg bg-[#F59E0B] px-4 py-2 text-sm font-bold text-neutral-950 hover:bg-[#EAB308] disabled:opacity-50">
            {inviteBusy ? <Loader2 className="h-4 w-4 animate-spin"/> : <Send className="h-4 w-4"/>}
            {inviteBusy ? "Generating…" : "Generate claim link"}
          </button>
        </div>

        {invites.length > 0 && (
          <ul className="mt-4 space-y-2">
            {invites.map(inv => (
              <li key={inv.token} className="rounded-lg border border-neutral-800 bg-neutral-950 p-3" data-testid={`admin-invite-row-${inv.invite_id}`}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-neutral-200">
                      {inv.founder_email || <span className="text-neutral-500 italic">No email tied — any founder login can claim</span>}
                    </p>
                    <p className="text-[11px] text-neutral-500">
                      Created {new Date(inv.created_at).toLocaleString()} · Expires {new Date(inv.expires_at).toLocaleString()}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {inv.claimed ? (
                      <span className="rounded-full bg-green-500/15 px-2.5 py-0.5 text-[11px] font-bold uppercase text-green-400" data-testid={`admin-invite-claimed-${inv.invite_id}`}>
                        Claimed
                      </span>
                    ) : (
                      <>
                        <button onClick={() => copyLink(inv.token)}
                          className="inline-flex items-center gap-1 rounded-lg border border-neutral-800 bg-neutral-900 px-3 py-1.5 text-xs font-semibold text-neutral-200 hover:bg-neutral-800"
                          data-testid={`admin-invite-copy-${inv.invite_id}`}>
                          {copiedTok === inv.token ? (<><Check className="h-3.5 w-3.5 text-green-400"/> Copied</>) : (<><Copy className="h-3.5 w-3.5"/> Copy link</>)}
                        </button>
                        <button onClick={() => revokeInvite(inv.token)}
                          className="rounded-lg border border-red-900/40 bg-red-950/40 px-2.5 py-1.5 text-xs font-semibold text-red-400 hover:bg-red-900/50"
                          data-testid={`admin-invite-revoke-${inv.invite_id}`}>
                          <Trash2 className="h-3.5 w-3.5"/>
                        </button>
                      </>
                    )}
                  </div>
                </div>
                {!inv.claimed && (
                  <div className="mt-2 truncate rounded bg-neutral-900 px-2 py-1 font-mono text-[11px] text-neutral-500" data-testid={`admin-invite-url-${inv.invite_id}`}>
                    {claimUrl(inv.token)}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
      )}

      {/* Verification — admin-managed businesses only */}
      {!isFounderOwned && sme.status !== "verified" && (
        <div className="mt-6 rounded-2xl border border-neutral-800 bg-neutral-900 p-6">
          <h2 className="text-lg font-bold">Verify business</h2>
          <p className="mt-1 text-sm text-neutral-400">Once verified, the SME is visible to funders.</p>
          <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-3">
            <Field label="Legal name">
              <input value={verify.legal_name} onChange={e => setVerify({...verify, legal_name: e.target.value})} className={ipt} data-testid="admin-biz-verify-legal"/>
            </Field>
            <Field label="Registration #">
              <input value={verify.registration_number} onChange={e => setVerify({...verify, registration_number: e.target.value})} className={ipt} data-testid="admin-biz-verify-regno"/>
            </Field>
            <Field label="Director name">
              <input value={verify.director_name} onChange={e => setVerify({...verify, director_name: e.target.value})} className={ipt} data-testid="admin-biz-verify-dir"/>
            </Field>
          </div>
          <div className="mt-4">
            <button onClick={runVerify} disabled={busy || !verify.legal_name} data-testid="admin-biz-verify-btn"
              className="inline-flex items-center gap-1.5 rounded-lg bg-green-600 px-5 py-2 text-sm font-bold text-neutral-100 hover:bg-green-500 disabled:opacity-50">
              <ShieldCheck className="h-4 w-4"/>{busy ? "Verifying…" : "Verify business"}
            </button>
          </div>
        </div>
      )}

      {/* Account audit log — actor-attributed activity incl. advisor actions */}
      <AuditLogSection companyId={id} />

      {/* Email journey — every automated email + open tracking */}
      {owner?.user_id && <EmailJourneySection userId={owner.user_id} />}
    </AdminShell>
  );
}

function EmailJourneySection({ userId }) {
  const [entries, setEntries] = useState(null);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!open || entries !== null) return;
    api.get(`/admin/founders/${userId}/email-journey`)
      .then((r) => setEntries(r.data?.entries || []))
      .catch(() => setEntries([]));
  }, [open, entries, userId]);
  const fmt = (iso) => {
    try {
      return new Date(iso).toLocaleString("en-GB", {
        day: "2-digit", month: "short", year: "numeric",
        hour: "2-digit", minute: "2-digit",
      });
    } catch { return iso; }
  };
  return (
    <div className="mt-6 rounded-2xl border border-neutral-800 bg-neutral-900 p-5" data-testid="admin-biz-email-journey">
      <button type="button" onClick={() => setOpen((o) => !o)} data-testid="admin-biz-email-journey-toggle"
        className="text-sm font-bold text-white hover:text-[#7BD3CE]">
        Email journey {open ? "▾" : "▸"}
      </button>
      {open && (
        entries === null ? (
          <p className="mt-3 text-xs text-neutral-500">Loading…</p>
        ) : entries.length === 0 ? (
          <p className="mt-3 text-xs text-neutral-500">No automated emails recorded for this founder yet.</p>
        ) : (
          <>
            <ul className="mt-3 max-h-96 space-y-1.5 overflow-y-auto">
              {entries.map((e, i) => (
                <li key={i} className="flex flex-wrap items-baseline gap-x-3 text-xs" data-testid={`admin-email-journey-row-${i}`}>
                  <span className="w-36 shrink-0 text-neutral-500">{fmt(e.created_at)}</span>
                  <span className="text-neutral-200">{e.subject}</span>
                  {e.legacy ? (
                    <span className="rounded-full bg-neutral-800 px-2 py-0.5 text-[10px] font-bold text-neutral-500" title="Sent before open-tracking existed — from the profile flag">no tracking (pre-log)</span>
                  ) : e.opened_at ? (
                    <span className="rounded-full bg-emerald-500/15 px-2 py-0.5 text-[10px] font-bold text-emerald-400">Opened · {fmt(e.opened_at)}</span>
                  ) : e.trackable ? (
                    <span className="rounded-full bg-neutral-800 px-2 py-0.5 text-[10px] font-bold text-neutral-400">Not opened yet</span>
                  ) : (
                    <span className="rounded-full bg-neutral-800 px-2 py-0.5 text-[10px] font-bold text-neutral-500" title="Plain-text email — opens can't be tracked without an HTML pixel">no tracking (plain text)</span>
                  )}
                </li>
              ))}
            </ul>
            <p className="mt-3 text-[11px] text-neutral-600">
              Opens are pixel-based: some mail clients (e.g. Apple Mail privacy mode) pre-fetch or block images, so treat "Opened" as a strong signal, not a guarantee.
            </p>
          </>
        )
      )}
    </div>
  );
}

function AuditLogSection({ companyId }) {
  const [entries, setEntries] = useState(null);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!open || entries !== null) return;
    api.get(`/admin/businesses/${companyId}/audit-log`)
      .then((r) => setEntries(r.data?.entries || []))
      .catch(() => setEntries([]));
  }, [open, entries, companyId]);
  const fmt = (iso) => {
    try {
      return new Date(iso).toLocaleString("en-GB", {
        day: "2-digit", month: "short", year: "numeric",
        hour: "2-digit", minute: "2-digit",
      });
    } catch { return iso; }
  };
  return (
    <div className="mt-6 rounded-2xl border border-neutral-800 bg-neutral-900 p-5" data-testid="admin-biz-audit-log">
      <button type="button" onClick={() => setOpen((o) => !o)} data-testid="admin-biz-audit-toggle"
        className="text-sm font-bold text-white hover:text-[#7BD3CE]">
        Account audit log {open ? "▾" : "▸"}
      </button>
      {open && (
        entries === null ? (
          <p className="mt-3 text-xs text-neutral-500">Loading…</p>
        ) : entries.length === 0 ? (
          <p className="mt-3 text-xs text-neutral-500">No activity recorded yet.</p>
        ) : (
          <ul className="mt-3 max-h-96 space-y-1.5 overflow-y-auto">
            {entries.map((e, i) => (
              <li key={i} className="flex flex-wrap items-baseline gap-x-3 text-xs" data-testid={`admin-audit-row-${i}`}>
                <span className="w-36 shrink-0 text-neutral-500">{fmt(e.at)}</span>
                <span className={"shrink-0 font-bold " + (e.actor === "Founder" ? "text-[#7BD3CE]" : "text-purple-300")}>{e.actor}</span>
                <span className="text-neutral-300">{e.action}</span>
              </li>
            ))}
          </ul>
        )
      )}
    </div>
  );
}
